<script setup>
import AccountDialog from '@/components/dialogs/AccountDialog.vue'
import { lsGet, lsSet } from '@/helpers/localStorageHelper'
import { useAccountStore } from '@/stores/account'
import { useSnackBarStore } from '@/stores/snackbar'
import { useRouter } from 'vue-router'
import i18n from '@/plugins/i18n'

const { t } = i18n.global
const router = useRouter()
const snackbar = useSnackBarStore()

const showUserDialog = ref(false)
const showPasswordDialog = ref(false)
const savePasswordLoading = ref(false)
const userData = lsGet('userData', true) || null
const account = useAccountStore()

const isPasswordVisible = ref(false)

const passwordForm = ref({
  oldPassword: '',
  newPassword: '',
  confirmPassword: '',
})

const logout = async () => {
  await account
    .logout()
    .then(r => {
      snackbar.success(t('logoutSuccess'))
      router.push('/login')
    })
    .catch(snackbar.responseError)
}

function saveUserDialog(user) {
  showUserDialog.value = false
  lsSet('userData', user, true)
}
function openUserDialog() {
  showUserDialog.value = true
}
function closeUserDialog() {
  showUserDialog.value = false
}

const savePassword = async () => {
  savePasswordLoading.value = true
  if (passwordForm.value.newPassword !== passwordForm.value.confirmPassword) {
    snackbar.error(t('newPassAndConfirmPassNotMatch'))

    return
  }

  await account
    .changePassword({ password: passwordForm.value.newPassword, old_password: passwordForm.value.oldPassword })
    .then(r => {
      closePasswordDialog()
      snackbar.success(t('passwordChangedSuccess'))
    })
    .catch(snackbar.responseError)

  savePasswordLoading.value = false
}

function openPasswordDialog() {
  passwordForm.value = {
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  }
  showPasswordDialog.value = true
}
function closePasswordDialog() {
  passwordForm.value = {
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  }
  showPasswordDialog.value = false
}
</script>

<template>
  <VBadge dot location="bottom right" offset-x="3" offset-y="3" bordered color="success">
    <VAvatar class="cursor-pointer" color="primary" variant="tonal">
      <VImg :src="userData?.account?.avatar?.thumb_url || '/assets/imgs/avatar.webp'" />

      <!-- SECTION Menu -->
      <VMenu activator="parent" width="230" location="bottom end" offset="14px">
        <VList>
          <!-- 👉 User Avatar & Name -->
          <VListItem>
            <template #prepend>
              <VListItemAction start>
                <VBadge dot location="bottom right" offset-x="3" offset-y="3" color="success">
                  <VAvatar color="primary" variant="tonal">
                    <VImg :src="userData?.account?.avatar?.thumb_url || '/assets/imgs/avatar.webp'" />
                  </VAvatar>
                </VBadge>
              </VListItemAction>
            </template>

            <VListItemTitle class="font-weight-semibold">
              {{ userData?.account?.name || 'Admin' }}
            </VListItemTitle>
            <VListItemSubtitle>{{ userData?.account?.name || 'Admin' }}</VListItemSubtitle>
          </VListItem>

          <VDivider class="my-2" />

          <!-- 👉 Profile -->
          <VListItem link @click="openUserDialog">
            <template #prepend>
              <VIcon class="me-2" icon="mdi-account-outline" size="22" />
            </template>

            <VListItemTitle>{{ t('profile') }}</VListItemTitle>
          </VListItem>

          <!-- 👉 Change Password -->
          <VListItem link @click="openPasswordDialog">
            <template #prepend>
              <VIcon class="me-2" icon="mdi-key" size="22" />
            </template>

            <VListItemTitle>{{ t('changePassword') }}</VListItemTitle>
          </VListItem>

          <!-- Divider -->
          <VDivider class="my-2" />

          <!-- 👉 Logout -->
          <VListItem link @click="logout">
            <template #prepend>
              <VIcon class="me-2" icon="mdi-logout" size="22" />
            </template>

            <VListItemTitle>{{ t('logout') }}</VListItemTitle>
          </VListItem>
        </VList>
      </VMenu>
      <!-- !SECTION -->
    </VAvatar>
  </VBadge>

  <AccountDialog
    :selected-account="userData"
    account-title="editUser"
    account-type="profile"
    :open-account-dialog="showUserDialog"
    @save="saveUserDialog"
    @close="closeUserDialog" />

  <BaseDialog v-if="showPasswordDialog" title="changePassword" :loading="savePasswordLoading" @close="closePasswordDialog" @save="savePassword">
    <template #content>
      <VRow>
        <VCol>
          <VForm ref="changePasswordForm">
            <VRow>
              <VCol cols="12">
                <VLabel> {{ $t('oldPassword') }} <span>*</span> </VLabel>
                <VTextField
                  v-model="passwordForm.oldPassword"
                  variant="outlined"
                  density="compact"
                  :type="isPasswordVisible ? 'text' : 'password'"
                  :append-inner-icon="isPasswordVisible ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
                  @click:append-inner="isPasswordVisible = !isPasswordVisible" />
              </VCol>
            </VRow>
            <VRow>
              <VCol cols="12">
                <VLabel> {{ $t('newPassword') }} <span>*</span> </VLabel>
                <VTextField
                  v-model="passwordForm.newPassword"
                  variant="outlined"
                  density="compact"
                  :type="isPasswordVisible ? 'text' : 'password'"
                  :append-inner-icon="isPasswordVisible ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
                  @click:append-inner="isPasswordVisible = !isPasswordVisible" />
              </VCol>
            </VRow>
            <VRow>
              <VCol cols="12">
                <VLabel> {{ $t('confirmPassword') }} <span>*</span> </VLabel>
                <VTextField
                  v-model="passwordForm.confirmPassword"
                  variant="outlined"
                  density="compact"
                  :type="isPasswordVisible ? 'text' : 'password'"
                  :append-inner-icon="isPasswordVisible ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
                  @click:append-inner="isPasswordVisible = !isPasswordVisible" />
              </VCol>
            </VRow>
          </VForm>
        </VCol>
      </VRow>
    </template>
  </BaseDialog>
</template>
